import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Route, MapPin, Mountain, Clock, Crown, AlertCircle, Plus, Bike, Footprints, Car } from 'lucide-react';
import './MyRoutesPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MyRoutesPage() {
  const { t } = useTranslation();
  const { user, token, isPremium } = useAuth();
  const navigate = useNavigate();
  
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: '/my-routes' } });
      return;
    }
    
    if (!isPremium) {
      return;
    }
    
    fetchMyRoutes();
  }, [user, isPremium, token, navigate]);

  const fetchMyRoutes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/routes/user/my-routes`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setRoutes(response.data);
    } catch (error) {
      console.error('Error fetching my routes:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="my-routes-page">
        <div className="auth-required">
          <AlertCircle size={48} />
          <h2>Anmeldung erforderlich</h2>
          <p>Bitte melde dich an, um deine Routen zu sehen.</p>
          <Link to="/login" className="login-button">
            Jetzt anmelden
          </Link>
        </div>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className="my-routes-page">
        <div className="premium-required">
          <Crown size={64} />
          <h2>Premium Feature</h2>
          <p>Eigene Routen erstellen und verwalten ist nur für Premium-Mitglieder verfügbar.</p>
          <Link to="/profile" className="upgrade-button">
            Jetzt auf Premium upgraden
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Lade deine Routen...</p>
      </div>
    );
  }

  return (
    <div className="my-routes-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">
            <Route size={32} />
            Meine Routen
          </h1>
          <p className="page-subtitle">
            {routes.length} {routes.length === 1 ? 'Route' : 'Routen'} erstellt
          </p>
        </div>
        <Link to="/create-route" className="create-route-btn">
          <Plus size={20} />
          Neue Route erstellen
        </Link>
      </header>

      {routes.length === 0 ? (
        <div className="empty-state">
          <Route size={64} />
          <h2>Noch keine Routen erstellt</h2>
          <p>Erstelle deine erste eigene Route und teile sie mit der Community!</p>
          <Link to="/create-route" className="create-first-route-btn">
            <Plus size={20} />
            Erste Route erstellen
          </Link>
        </div>
      ) : (
        <div className="routes-grid">
          {routes.map((route) => (
            <div key={route.id} className="route-card">
              <div className="route-card-header">
                <div>
                  {route.activity_type && (
                    <span className={`route-activity activity-${route.activity_type}`}>
                      {route.activity_type === 'cycling' && <Bike size={14} />}
                      {route.activity_type === 'hiking' && <Footprints size={14} />}
                      {route.activity_type === 'roadtrip' && <Car size={14} />}
                      {t(`activities.${route.activity_type}`)}
                    </span>
                  )}
                  <h3 className="route-name">{route.name}</h3>
                </div>
                <span className={`difficulty-badge ${route.difficulty}`}>
                  {t(`difficulties.${route.difficulty}`)}
                </span>
              </div>

              <p className="route-description">{route.description}</p>

              <div className="route-stats">
                <div className="route-stat">
                  <MapPin size={18} />
                  <span>{route.distance_km} km</span>
                </div>
                <div className="route-stat">
                  <Mountain size={18} />
                  <span>{route.elevation_gain_m} m</span>
                </div>
                <div className="route-stat">
                  <Clock size={18} />
                  <span>{route.duration_hours.toFixed(2)} Std.</span>
                </div>
              </div>

              <div className="route-meta">
                <span className="route-region">{route.region}</span>
                <span className="route-type">{t(`route_types.${route.route_type}`)}</span>
              </div>

              {route.tags && route.tags.length > 0 && (
                <div className="route-tags">
                  {route.tags.map((tag, idx) => (
                    <span key={idx} className="tag">{tag}</span>
                  ))}
                </div>
              )}

              <div className="route-card-footer">
                <span className="created-date">
                  Erstellt am {new Date(route.created_at).toLocaleDateString('de-DE')}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
